/**
 * Parsing for the acknowledgement coefficient.io posts back after calling Meta's Conversions API.
 *
 * Shared because both surfaces that frame the attribution pages -- the add-on and the webapp --
 * have to validate the same message, and an origin allowlist that drifts between them is a bug
 * nobody would notice until Meta numbers stopped reconciling. The frame host itself stays with
 * each surface: they detect their environment differently.
 */

/**
 * Who we accept an attribution message from.
 *
 * Both environments are listed unconditionally. This set answers "did one of our own attribution
 * pages send this", not "which environment is this build"; a surface only ever frames the host its
 * own build points at, so carrying the other environment's origin costs nothing and keeps the
 * check from silently dropping every acknowledgement in a build pointed somewhere else.
 */
export const COEFFICIENT_ATTRIBUTION_ORIGINS = new Set([
  "https://coefficient.io",
  "https://www.coefficient.io",
  "https://coeffstaging.wpengine.com",
]);

export const ATTRIBUTION_DELIVERY_MESSAGE_TYPE = "coefficient_attribution_delivery_result";

type AttributionDeliveryStatus = "accepted" | "rejected" | "unknown";

export type AttributionDeliveryResult = {
  attribution_event_name: string;
  /** Whether the browser (pixel) leg fired. A missing dedup match in Meta is expected without it. */
  browser_event_sent?: boolean;
  delivery_status: AttributionDeliveryStatus;
  destination: "meta_capi";
  /** Match keys present on the request, as booleans — never the values themselves. */
  has_fbc?: boolean;
  has_fbp?: boolean;
  http_status?: number;
  /**
   * Renamed from the `error` and `has_email` that coefficient.io puts on the wire. Analytics
   * property names are global across every event, and a bare `error` would be claimed by the
   * first unrelated event that wants one, while `has_email` already means "this smart alert
   * delivers over email" — see `AnalyticsEvent` in spec.py.
   */
  meta_delivery_error?: string;
  meta_event_id: string;
  meta_event_name: string;
  meta_has_email?: boolean;
  sent_at?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Copy a diagnostic through only when it has the type we expect, and drop it otherwise.
 *
 * These fields exist to explain a delivery after the fact, so an unexpected one is worth losing on
 * its own — never at the cost of the delivery record carrying it. coefficient.io initialises them
 * to `null` and overwrites only the ones it has a value for, so `null` means absent; `http_status`
 * is the case that matters, staying `null` when the request never completed, which is exactly the
 * `delivery_status: "unknown"` message we most need to see.
 */
const optional = <T>(value: unknown, type: "boolean" | "number" | "string"): T | undefined =>
  typeof value === type ? (value as T) : undefined;

/**
 * Validate the small, non-PII acknowledgement sent by coefficient.io after it calls Meta CAPI.
 * The iframe may post either an object or a JSON string, depending on which legacy page hosts it.
 */
export const parseAttributionDeliveryResult = (
  event: Pick<MessageEvent, "data" | "origin">
): AttributionDeliveryResult | null => {
  if (!COEFFICIENT_ATTRIBUTION_ORIGINS.has(event.origin)) {
    return null;
  }

  let data: unknown = event.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (_error) {
      return null;
    }
  }

  if (!isRecord(data) || data.type !== ATTRIBUTION_DELIVERY_MESSAGE_TYPE) {
    return null;
  }

  const validStatuses: AttributionDeliveryStatus[] = ["accepted", "rejected", "unknown"];
  if (
    data.destination !== "meta_capi" ||
    typeof data.attribution_event_name !== "string" ||
    typeof data.meta_event_name !== "string" ||
    typeof data.meta_event_id !== "string" ||
    typeof data.delivery_status !== "string" ||
    !validStatuses.includes(data.delivery_status as AttributionDeliveryStatus)
  ) {
    return null;
  }

  return {
    attribution_event_name: data.attribution_event_name,
    browser_event_sent: optional<boolean>(data.browser_event_sent, "boolean"),
    delivery_status: data.delivery_status as AttributionDeliveryStatus,
    destination: "meta_capi",
    has_fbc: optional<boolean>(data.has_fbc, "boolean"),
    has_fbp: optional<boolean>(data.has_fbp, "boolean"),
    http_status: optional<number>(data.http_status, "number"),
    meta_delivery_error: optional<string>(data.error, "string"),
    meta_event_id: data.meta_event_id,
    meta_event_name: data.meta_event_name,
    meta_has_email: optional<boolean>(data.has_email, "boolean"),
    sent_at: optional<string>(data.sent_at, "string"),
  };
};
